
import {
    Boxes,
    ChevronDown,
    CircleDollarSign,
    Gamepad2,
    Gauge,
    Gift,
    HandCoins,
    Landmark,
    MapPin,
    MessageCircleMore,
    Network,
    PackageCheck,
    Search,
    ShoppingBag,
    Soup,
    Star,
    UserCircle2,
    WalletCards,
    X,
} from "lucide-react";
import {
    ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    useLocation,
    useNavigate,
} from "react-router-dom";

import {
    USER_SIDEBAR_ITEMS,
    UserSidebarIcon,
    UserSidebarItem,
} from "./UserSidebarData";

import "./UserSidebar.css";

type UserSidebarProps = {
    open: boolean;
    onClose: () => void;
};

const normalizePath = (
    pathname: string
) => {
    if (!pathname) {
        return "/";
    }

    return pathname.length > 1
        ? pathname.replace(/\/+$/, "")
        : pathname;
};

const routeMatches = (
    currentPath: string,
    route?: string
) => {
    if (!route) {
        return false;
    }

    const current =
        normalizePath(currentPath).toLowerCase();

    const target =
        normalizePath(route).toLowerCase();

    return (
        current === target ||
        current.startsWith(`${target}/`)
    );
};

const renderSidebarIcon = (
    icon: UserSidebarIcon
): ReactNode => {
    if (icon.type === "bootstrap") {
        return (
            <i
                className={icon.className}
                aria-hidden="true"
            />
        );
    }

    const commonProps = {
        size: 18,
        strokeWidth: 1.9,
    };

    switch (icon.name) {
        case "dashboard":
            return <Gauge {...commonProps} />;

        case "account":
            return <UserCircle2 {...commonProps} />;

        case "network":
            return <Network {...commonProps} />;

        case "address":
            return <MapPin {...commonProps} />;

        case "portfolio":
            return <Boxes {...commonProps} />;

        case "orders":
            return <PackageCheck {...commonProps} />;

        case "food":
            return <Soup {...commonProps} />;

        case "stuff":
            return <Gift {...commonProps} />;

        case "referral":
            return <HandCoins {...commonProps} />;

        case "earnings":
            return <CircleDollarSign {...commonProps} />;

        case "wallet":
            return <WalletCards {...commonProps} />;

        case "withdrawal":
            return <Landmark {...commonProps} />;

        case "chat":
            return <MessageCircleMore {...commonProps} />;

        case "testimonial":
            return <Star {...commonProps} />;

        case "games":
            return <Gamepad2 {...commonProps} />;

        default:
            return <ShoppingBag {...commonProps} />;
    }
};

const UserSidebar = ({
    open,
    onClose,
}: UserSidebarProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [
        searchValue,
        setSearchValue,
    ] = useState("");

    const [
        openGroups,
        setOpenGroups,
    ] = useState<string[]>([]);

    const activeParentIds =
        useMemo(
            () =>
                USER_SIDEBAR_ITEMS.filter(
                    (item) =>
                        item.children?.some(
                            (child) =>
                                routeMatches(
                                    location.pathname,
                                    child.route
                                )
                        )
                ).map((item) => item.id),
            [location.pathname]
        );

    useEffect(() => {
        if (activeParentIds.length === 0) {
            return;
        }

        setOpenGroups((previous) =>
            Array.from(
                new Set([
                    ...previous,
                    ...activeParentIds,
                ])
            )
        );
    }, [activeParentIds]);

    useEffect(() => {
        const handleEscape = (
            event: KeyboardEvent
        ) => {
            if (
                event.key === "Escape" &&
                open
            ) {
                onClose();
            }
        };

        window.addEventListener(
            "keydown",
            handleEscape
        );

        return () =>
            window.removeEventListener(
                "keydown",
                handleEscape
            );
    }, [onClose, open]);

    useEffect(() => {
        const mediaQuery =
            window.matchMedia(
                "(max-width: 860px)"
            );

        if (
            !open ||
            !mediaQuery.matches
        ) {
            return;
        }

        const previousOverflow =
            document.body.style.overflow;

        document.body.style.overflow =
            "hidden";

        return () => {
            document.body.style.overflow =
                previousOverflow;
        };
    }, [open]);

    const normalizedSearch =
        searchValue
            .trim()
            .toLowerCase();

    const filteredItems =
        useMemo(() => {
            if (!normalizedSearch) {
                return USER_SIDEBAR_ITEMS;
            }

            return USER_SIDEBAR_ITEMS.reduce<
                UserSidebarItem[]
            >(
                (result, item) => {
                    const parentTerms = [
                        item.label,
                        ...(item.searchTerms ?? []),
                    ]
                        .join(" ")
                        .toLowerCase();

                    const parentMatches =
                        parentTerms.includes(
                            normalizedSearch
                        );

                    const matchingChildren =
                        item.children?.filter(
                            (child) =>
                                [
                                    child.label,
                                    ...(child.searchTerms ??
                                        []),
                                ]
                                    .join(" ")
                                    .toLowerCase()
                                    .includes(
                                        normalizedSearch
                                    )
                        );

                    if (parentMatches) {
                        result.push(item);
                        return result;
                    }

                    if (
                        matchingChildren?.length
                    ) {
                        result.push({
                            ...item,
                            children:
                                matchingChildren,
                        });
                    }

                    return result;
                },
                []
            );
        }, [normalizedSearch]);

    useEffect(() => {
        if (!normalizedSearch) {
            return;
        }

        const searchableGroups =
            filteredItems
                .filter(
                    (item) =>
                        item.children?.length
                )
                .map((item) => item.id);

        setOpenGroups(searchableGroups);
    }, [
        normalizedSearch,
        filteredItems,
    ]);

    const toggleGroup = (
        id: string
    ) => {
        setOpenGroups((previous) =>
            previous.includes(id)
                ? previous.filter(
                    (item) => item !== id
                )
                : [...previous, id]
        );
    };

    const handleNavigate = (
        route: string
    ) => {
        const alreadyActive =
            routeMatches(
                location.pathname,
                route
            );

        if (!alreadyActive) {
            navigate(route);
        }

        /*
         * DO NOT call onClose() here.
         *
         * This is the important fix.
         * Earlier mobile navigation automatically closed
         * the sidebar. Now it remains open until the user
         * closes it manually.
         */
    };

    return (
        <>
            <button
                type="button"
                className={`user-sidebar-backdrop ${open ? "show" : ""
                    }`}
                onClick={onClose}
                aria-label="Close sidebar"
            />

            <aside
                className={`user-sidebar-drawer ${open ? "open" : ""
                    }`}
                aria-label="User dashboard navigation"
                aria-hidden={!open}
            >
                <div className="user-sidebar-head">
                    <div className="user-sidebar-search">
                        <Search
                            size={18}
                            strokeWidth={1.9}
                        />

                        <input
                            type="text"
                            inputMode="search"
                            value={searchValue}
                            onChange={(event) =>
                                setSearchValue(
                                    event.target.value
                                )
                            }
                            placeholder="Search menu..."
                            aria-label="Search user menu"
                            autoComplete="off"
                        />

                        {searchValue && (
                            <button
                                type="button"
                                onClick={() =>
                                    setSearchValue("")
                                }
                                aria-label="Clear menu search"
                            >
                                <X
                                    size={15}
                                    strokeWidth={2}
                                />
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close sidebar"
                    >
                        <X
                            size={18}
                            strokeWidth={2}
                        />
                    </button>
                </div>

               

                <nav className="user-sidebar-nav">
                    {filteredItems.length >
                        0 ? (
                        filteredItems.map(
                            (item) => {
                                const hasChildren =
                                    Boolean(
                                        item.children
                                            ?.length
                                    );

                                const groupOpen =
                                    openGroups.includes(
                                        item.id
                                    );

                                const directActive =
                                    routeMatches(
                                        location.pathname,
                                        item.route
                                    );

                                const childActive =
                                    item.children?.some(
                                        (child) =>
                                            routeMatches(
                                                location.pathname,
                                                child.route
                                            )
                                    ) ?? false;

                                return (
                                    <div
                                        className={`user-sidebar-item ${childActive
                                            ? "has-active-child"
                                            : ""
                                            }`}
                                        key={item.id}
                                    >
                                        <button
                                            type="button"
                                            className={`user-sidebar-main-button ${directActive
                                                ? "active"
                                                : ""
                                                } ${childActive
                                                    ? "active-parent"
                                                    : ""
                                                }`}
                                            onClick={() => {
                                                if (
                                                    hasChildren
                                                ) {
                                                    toggleGroup(
                                                        item.id
                                                    );
                                                    return;
                                                }

                                                if (
                                                    item.route
                                                ) {
                                                    handleNavigate(
                                                        item.route
                                                    );
                                                }
                                            }}
                                            aria-expanded={
                                                hasChildren
                                                    ? groupOpen
                                                    : undefined
                                            }
                                        >
                                            <span className="user-sidebar-main-icon">
                                                {renderSidebarIcon(
                                                    item.icon
                                                )}
                                            </span>

                                            <span className="user-sidebar-main-label">
                                                {
                                                    item.label
                                                }
                                            </span>

                                            {hasChildren && (
                                                <ChevronDown
                                                    size={
                                                        16
                                                    }
                                                    strokeWidth={
                                                        2
                                                    }
                                                    className={`user-sidebar-chevron ${groupOpen
                                                        ? "open"
                                                        : ""
                                                        }`}
                                                />
                                            )}
                                        </button>

                                        {hasChildren &&
                                            groupOpen && (
                                                <div className="user-sidebar-tree">
                                                    {item.children?.map(
                                                        (
                                                            child
                                                        ) => {
                                                            const active =
                                                                routeMatches(
                                                                    location.pathname,
                                                                    child.route
                                                                );

                                                            return (
                                                                <button
                                                                    type="button"
                                                                    className={`user-sidebar-child-button ${active
                                                                        ? "active"
                                                                        : ""
                                                                        }`}
                                                                    key={
                                                                        child.id
                                                                    }
                                                                    onClick={() =>
                                                                        handleNavigate(
                                                                            child.route
                                                                        )
                                                                    }
                                                                >
                                                                    <span className="user-sidebar-child-node">
                                                                        <span />
                                                                    </span>

                                                                    <span className="user-sidebar-child-label">
                                                                        {
                                                                            child.label
                                                                        }
                                                                    </span>
                                                                </button>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                );
                            }
                        )
                    ) : (
                        <div className="user-sidebar-empty">
                            <Search
                                size={22}
                                strokeWidth={1.8}
                            />

                            <strong>
                                No menu found
                            </strong>

                            <span>
                                Try another keyword.
                            </span>
                        </div>
                    )}
                </nav>
            </aside>
        </>
    );
};

export default UserSidebar;
